import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { AuthFormShell } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import { PasswordInput } from '@/forms/PasswordInput'
// import { PasswordStrengthBar } from '@/forms/components/PasswordStrengthBar'
// import { usePasswordStrength } from '@/hooks/usePasswordStrength'
import API from '@/lib/api'
import { passwordMinLength } from '@/config/passwordMinLength'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

interface SetPasswordFormProps {
  action: string
  code: string | null
  email: string | null
  onSuccess?: (response: unknown) => void
  simple?: boolean
}

interface SetPasswordFormValues {
  code: string
  email: string
  password: string
  passwordConfirmation: string
}

const schema = yup.object().shape({
  code: yup.string().required('resetPassword.fields.code.errors.required'),
  email: yup.string().email('resetPassword.fields.email.errors.requiredYup').required('resetPassword.fields.email.errors.requiredYup'),
  password: yup.string().required('resetPassword.fields.password.errors.requiredYup'),
  passwordConfirmation: yup.string().required('resetPassword.fields.passwordConfirmation.errors.requiredYup'),
})

const resolver = async (values: SetPasswordFormValues) => {
  const errors: Record<string, { type: string; message: string }> = {}

  if (values.password.length < passwordMinLength) {
    errors.password = {
      type: 'length',
      message: 'resetPassword.fields.password.errors.minLength',
    }
  }

  if (values.password !== values.passwordConfirmation) {
    errors.passwordConfirmation = {
      type: 'mismatch',
      message: 'resetPassword.fields.passwordConfirmation.errors.mismatch',
    }
  }

  if (
    values.email &&
    values.email !== '' &&
    values.password.indexOf(values?.email) !== -1
  ) {
    errors.password = {
      type: 'email',
      message: 'resetPassword.fields.password.errors.containsEmail',
    }
  }

  return {
    values,
    errors,
  }
}

export const SetPasswordForm = ({
  action,
  code,
  email,
  onSuccess,
  simple,
}: SetPasswordFormProps) => {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    handleSubmit,
    register,
    getValues,
    setValue,
    formState: { errors },
    watch,
  } = useForm<SetPasswordFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      code: code ?? '',
      email: email ?? '',
    },
  })
  const password = watch('password')
  // const passwordStrengthResult = usePasswordStrength(password || '')

  const onSubmit = (values: SetPasswordFormValues) => {
    if (isSubmitting) return
    if (Object.keys(errors).length > 0) return
    if (!values.password || !values.passwordConfirmation) return

    if (values.password !== values.passwordConfirmation) {
      return toast.error(t('resetPassword.mismatchToast.title'), {
        description: t('resetPassword.mismatchToast.description'),
        duration: 5000,
        closeButton: true,
      })
    }

    setIsSubmitting(true)

    API.post(action, {
      email: values.email,
      token: values.code,
      password: values.password,
    })
      .then((response) => {
        setIsSubmitting(false)
        onSuccess?.(response)
      })
      .catch((error) => {
        setIsSubmitting(false)
        console.error(error)
        return error
      })
  }

  return (
    <AuthFormShell title={t('resetPassword.title')} description={t('resetPassword.description')}>
    <form
      method="POST"
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-6"
    >
      <input type="hidden" {...register('code')} />
      {email && <input type="hidden" {...register('email')} />}

      {!simple && (
        <div className="space-y-2">
          <Label htmlFor="code" className="font-label text-brand-secondary">{t('resetPassword.fields.code.label')}</Label>
          <InputOTP maxLength={6} {...register('code')} onChange={(value) => setValue('code', value)}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={1} />
              </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={3} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={4} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
          {errors.code && (
            <p className="text-sm text-destructive">{t(errors.code.message as string)}</p>
          )}
        </div>
      )}

      {!email && !simple && (
        <div className="space-y-2">
          <Label htmlFor="email" className="font-label text-brand-secondary">{t('resetPassword.fields.email.label')}</Label>
          <Input
            id="email"
            type={email ? 'hidden' : 'email'}
            placeholder={t('resetPassword.fields.email.placeholder')}
            disabled={!!email || isSubmitting}
            readOnly={!!email}
            autoComplete="email"
            {...register('email', {
              required: 'resetPassword.fields.email.errors.required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'resetPassword.fields.email.errors.invalid',
              },
            })}
          />
          {errors.email && (
            <p className="text-sm text-destructive">{t(errors.email.message as string)}</p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="password" className="font-label text-brand-secondary">{t('resetPassword.fields.password.label')}</Label>
        <PasswordInput
          id="password"
          placeholder={t('resetPassword.fields.password.placeholder')}
          disabled={isSubmitting}
          autoComplete="new-password"
          minLength={passwordMinLength}
          {...register('password', {
            required: 'resetPassword.fields.password.errors.required',
          })}
        />
        {errors.password && (
          <p className="text-sm text-destructive">
            {t(errors.password.message as string, { min: passwordMinLength })}
          </p>
        )}
        {/* <PasswordStrengthBar score={passwordStrengthResult?.score} /> */}
      </div>

      <div className="space-y-2">
        <Label htmlFor="passwordConfirmation" className="font-label text-brand-secondary">{t('resetPassword.fields.passwordConfirmation.label')}</Label>
        <PasswordInput
          id="passwordConfirmation"
          placeholder={t('resetPassword.fields.passwordConfirmation.placeholder')}
          disabled={isSubmitting}
          autoComplete="new-password"
          {...register('passwordConfirmation', {
            required: 'resetPassword.fields.passwordConfirmation.errors.required',
            validate: (value) =>
              value === getValues('password') || 'resetPassword.fields.passwordConfirmation.errors.mismatch',
          })}
        />
        {errors.passwordConfirmation && (
          <p className="text-sm text-destructive">
            {t(errors.passwordConfirmation.message as string)}
          </p>
        )}
      </div>

      <Button className="h-[50px] w-full rounded-md font-label" disabled={isSubmitting} type="submit">
        {isSubmitting ? t('resetPassword.submitting') : t('resetPassword.submit')}
      </Button>
    </form>
    </AuthFormShell>
  )
}
